import { getOffsetTop, slideDown, slideUp } from '../utils/functions';

interface SectionThreshold {
	element: HTMLElement;
	offsetTop: number;
	offsetBottom: number;
}

class MainHeader {
	header: HTMLDivElement;
	mainNavMenu: HTMLDivElement;
	adminBar: HTMLElement | null;
	notificationBar: HTMLDivElement;
	notificationBarClose: HTMLButtonElement | null;
	mobileMenuTrigger: HTMLElement | null;
	nonDarkSections: Array<HTMLDivElement>;
	sectionThresholds: Array<SectionThreshold>;
	megaMenuItems: NodeListOf<HTMLDivElement>;
	megaMenuBackBtns: NodeListOf<HTMLButtonElement>;
	megaMenuCategoryTriggers: NodeListOf<HTMLDivElement>;
	isTouchDevice: boolean;

	constructor() {
		this.header = document.querySelector('.main-header') as HTMLDivElement;
		this.mainNavMenu = document.querySelector('.main-header__nav-inner') as HTMLDivElement;
		this.adminBar = document.getElementById('wpadminbar');
		this.notificationBarClose = document.querySelector<HTMLButtonElement>(
			'.notification-bar__close'
		);
		this.notificationBar = document.querySelector<HTMLElement>(
			'.notification-bar'
		) as HTMLDivElement;
		this.mobileMenuTrigger = document.querySelector<HTMLButtonElement>('.mobile-menu-trigger');
		this.megaMenuItems = document.querySelectorAll<HTMLDivElement>(
			'.main-header .menu-item-object-mega_menu'
		);
		this.megaMenuCategoryTriggers = document.querySelectorAll<HTMLDivElement>(
			'.mega-menu-category__title'
		);
		this.nonDarkSections = Object.values(
			document.querySelectorAll<HTMLDivElement>(
				'.block-section:not(.bg-dark),.block-hero--bg-video'
			)
		);
		this.megaMenuBackBtns = document.querySelectorAll<HTMLButtonElement>(
			'button[data-mega-menu-back]'
		);
		this.sectionThresholds = [];

		this.isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
	}

	init() {
		this.setHeaderHeight();
		this.setSectionThresholds();

		window.addEventListener('resize', this.handleResizeEvent.bind(this), {
			passive: true,
		});
		window.addEventListener('scroll', this.handleScrollEvent.bind(this), {
			passive: true,
		});
		window.addEventListener('load', this.handleResizeEvent.bind(this), {
			passive: true,
		});
		window.addEventListener('slideAnimationEnd', this.handleResizeEvent.bind(this), {
			passive: true,
		});

		// Mega menu handling.
		this.megaMenuItems.forEach((item) => {
			item.addEventListener('mouseenter', this.handleMouseEnter.bind(this));
			item.addEventListener('mouseleave', this.handleMouseLeave.bind(this));
			item.querySelector('a')?.addEventListener('click', this.handleMegaMenuClick.bind(this));
		});

		if (null !== this.mobileMenuTrigger) {
			this.mobileMenuTrigger.addEventListener('click', this.mobileMenuToggle.bind(this));
		}

		this.megaMenuBackBtns.forEach((item) => {
			item.addEventListener('click', this.handleBackBtnClick.bind(this));
		});

		this.megaMenuCategoryTriggers.forEach((item) => {
			item.addEventListener('click', this.handleMenuCategoryClick.bind(this));
		});

		if (this.notificationBarClose) {
			this.notificationBarClose.addEventListener(
				'click',
				this.handleNotificationBarClose.bind(this)
			);
		}
	}

	handleMenuCategoryClick(e: Event) {
		if (window.matchMedia(`(min-width: 992px)`).matches) return;

		const target = e.currentTarget as HTMLDivElement;
		const parent = target.parentElement;
		if (null !== parent) {
			const links = parent.querySelector<HTMLUListElement>('.mega-menu-category__links');
			if (null !== links) {
				if (parent.classList.contains('active')) {
					slideUp(links);
					parent.classList.remove('active');
				} else {
					slideDown(links);
					parent.classList.add('active');
				}
			}
		}
	}

	handleBackBtnClick() {
		const activeMegaMenu = document.querySelector<HTMLDivElement>(
			'.menu-item-object-mega_menu.active'
		);

		if (null !== activeMegaMenu) {
			activeMegaMenu.classList.remove('active');
			this.closeMobileMegaMenu(activeMegaMenu);
		}
	}

	handleMouseEnter(e: Event) {
		if (this.isTouchDevice) return;

		document.body.classList.add('mega-menu-open');
		const target = e.currentTarget as HTMLDivElement;
		this.openMegaMenu(target);
	}

	handleMouseLeave(e: Event) {
		if (this.isTouchDevice) return;

		document.body.classList.remove('mega-menu-open');
		const target = e.currentTarget as HTMLDivElement;
		this.closeMegaMenu(target);
	}

	mobileMenuToggle() {
		if (document.body.classList.contains('mobile-menu-open')) {
			document.body.classList.remove('mobile-menu-open');
			document.body.classList.remove('mobile-mega-menu-open');
			this.handleBackBtnClick();
		} else {
			document.body.classList.add('mobile-menu-open');
		}
	}

	handleMegaMenuClick(e: Event) {
		if (window.matchMedia(`(max-width: 991px)`).matches) {
			e.preventDefault();

			const target = e.currentTarget as HTMLAnchorElement;
			if (target.parentElement) {
				if (target.parentElement.classList.contains('active')) {
					target.parentElement.classList.remove('active');

					this.closeMobileMegaMenu(target, true);
				} else {
					target.parentElement.classList.add('active');

					this.openMobileMegaMenu(target, true);
				}
			}
		} else if (this.isTouchDevice) {
			e.preventDefault();

			const target = e.currentTarget as HTMLAnchorElement;
			if (target.parentElement) {
				if (target.parentElement.classList.contains('active')) {
					target.parentElement.classList.remove('active');
					this.closeMegaMenu(target, true);
				} else {
					// Close active mega menu if it's open.
					const activeMegaMenu = document.querySelector<HTMLDivElement>(
						'.menu-item-object-mega_menu.active'
					);

					if (null !== activeMegaMenu) {
						activeMegaMenu.classList.remove('active');
						this.closeMegaMenu(activeMegaMenu);
					}

					target.parentElement.classList.add('active');
					this.openMegaMenu(target, true);
				}
			}
		}
	}

	openMobileMegaMenu(item: HTMLAnchorElement | HTMLDivElement, isLink = false) {
		const li = isLink ? item.parentElement : item;
		if (!li) return;

		const megaMenu = li.querySelector<HTMLDivElement>('.mega-menu-item');
		if (megaMenu) {
			megaMenu.classList.add('active');
			document.body.classList.add('mobile-mega-menu-open');
		}
	}

	closeMobileMegaMenu(item: HTMLAnchorElement | HTMLDivElement, isLink = false) {
		const li = isLink ? item.parentElement : item;
		if (!li) return;

		const megaMenu = li.querySelector<HTMLDivElement>('.mega-menu-item');
		if (megaMenu) {
			document.body.classList.add('mega-menu-closing');
			megaMenu.classList.add('is-closing');
			megaMenu.classList.remove('active');
			document.body.classList.remove('mobile-mega-menu-open');

			setTimeout(() => {
				document.body.classList.remove('mega-menu-closing');
				megaMenu.classList.remove('is-closing');
			}, 300);
		}
	}

	openMegaMenu(item: HTMLAnchorElement | HTMLDivElement, isLink = false) {
		if (window.matchMedia(`(max-width: 991px)`).matches) {
			return;
		}

		const li = isLink ? item.parentElement : item;
		if (!li) return;

		const megaMenu = li.querySelector<HTMLDivElement>('.mega-menu-item');
		if (megaMenu) {
			slideDown(megaMenu);
			megaMenu.classList.add('is-open');
		}
	}

	closeMegaMenu(item: HTMLAnchorElement | HTMLDivElement, isLink = false) {
		if (window.matchMedia(`(max-width: 991px)`).matches) {
			return;
		}

		const li = isLink ? item.parentElement : item;
		if (!li) return;

		const megaMenu = li.querySelector<HTMLDivElement>('.mega-menu-item');
		if (megaMenu) {
			slideUp(megaMenu);
			megaMenu.classList.remove('is-open');
		}
	}

	handleScrollEvent() {
		if (window.lockScrollAnimations) return;

		const activeSection = this.sectionThresholds.find((section) => {
			return window.scrollY >= section.offsetTop && window.scrollY <= section.offsetBottom;
		});

		if (activeSection) {
			this.header.classList.remove('main-header--show-bg');
		} else {
			this.header.classList.add('main-header--show-bg');
		}
	}

	handleResizeEvent() {
		if (window.lockScrollAnimations) return;

		this.setHeaderHeight();
		this.setSectionThresholds();
		this.handleScrollEvent();
	}

	setSectionThresholds() {
		let headerHeight: string = getComputedStyle(document.documentElement).getPropertyValue(
			'--headerHeightFull'
		);

		headerHeight = headerHeight.replace('px', '');

		this.sectionThresholds = this.nonDarkSections.map((section) => {
			const offsetTop = getOffsetTop(section);

			return {
				element: section,
				offsetTop: offsetTop - parseInt(headerHeight) - 40,
				offsetBottom: offsetTop + section.offsetHeight + 40,
			};
		});
	}

	setHeaderHeight() {
		const headerHeight = this.header.offsetHeight;
		let headerHeightFull = this.header.offsetHeight;

		if (null !== this.adminBar) {
			headerHeightFull += this.adminBar.offsetHeight;
		}

		document.documentElement.style.setProperty('--vh', `${window.innerHeight * 0.01}px`);
		document.documentElement.style.setProperty('--headerHeight', headerHeight + 'px');
		document.documentElement.style.setProperty('--headerHeightFull', headerHeightFull + 'px');
	}

	handleNotificationBarClose() {
		this.notificationBar.style.display = 'none';
	}
}

export default new MainHeader();
